import { z } from 'zod';
import {
  actorSnapshotSchema,
  entityIdSchema,
  nonEmptyTextSchema,
  resourceScopeSchema,
  sha256Schema,
  utcDateTimeSchema,
} from '@kop/contracts';

export const educationStages = [
  'draft',
  'validated',
  'submitted',
  'in-review',
  'returned',
  'resubmitted',
  'recorded',
  'approved',
  'closed',
] as const;
export const educationStageSchema = z.enum(educationStages);

export const statutoryEducationCategories = [
  'regular',
  'hiring',
  'work-change',
  'special',
  'construction-basic',
  'special-type-worker',
  'msds',
] as const;
export const statutoryEducationCategorySchema = z.enum(statutoryEducationCategories);

export const statutoryRecordEvidenceFieldIds = [
  'employer',
  'education-category',
  'audience',
  'session',
  'location',
  'instructor',
  'delivered-topics',
  'participant',
  'individual-attendance-proof',
  'committed-photo',
  'source-snapshot',
  'review-history',
] as const;

export const educationLegalSourceSchema = z
  .object({
    sourceId: entityIdSchema,
    authority: z.enum(['law-go-kr', 'moel', 'kosha']),
    title: nonEmptyTextSchema.max(300),
    provision: nonEmptyTextSchema.max(300),
    officialUrl: z.url().refine((value) => {
      const hostname = new URL(value).hostname.toLowerCase();
      return (
        hostname === 'law.go.kr' ||
        hostname === 'www.law.go.kr' ||
        hostname === 'moel.go.kr' ||
        hostname === 'www.moel.go.kr' ||
        hostname === 'kosha.or.kr' ||
        hostname === 'www.kosha.or.kr'
      );
    }, 'Legal source must be an official Korean government or KOSHA URL'),
    promulgationOrRevision: nonEmptyTextSchema.max(200),
    effectiveFrom: z.iso.date(),
    verifiedAt: utcDateTimeSchema,
    sourceArtifactSha256: sha256Schema.optional(),
    sourceLocator: nonEmptyTextSchema.max(300).optional(),
  })
  .strict();

export const educationTopicSchema = z
  .object({
    topicId: entityIdSchema,
    title: nonEmptyTextSchema.max(500),
    legalSourceIds: z.array(entityIdSchema).min(1),
  })
  .strict();

export const specialEducationWorkSchema = z
  .object({
    workCode: entityIdSchema,
    annexOrdinal: z.number().int().min(1).max(39),
    title: nonEmptyTextSchema.max(2_000),
    sourceLocator: nonEmptyTextSchema.max(300),
    legalSourceIds: z.array(entityIdSchema).min(1),
    individualTopics: z
      .array(
        z
          .object({
            topicId: entityIdSchema,
            title: nonEmptyTextSchema.max(1_000),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

/** A published snapshot is required. The application never embeds legal hours or category rules. */
export const educationStandardSnapshotSchema = z
  .object({
    standardId: entityIdSchema,
    versionId: entityIdSchema,
    title: nonEmptyTextSchema.max(300),
    sourceDocumentId: entityIdSchema,
    sourceRevision: nonEmptyTextSchema.max(100),
    effectiveFrom: z.iso.date(),
    effectiveUntil: z.iso.date().nullable(),
    publishedAt: utcDateTimeSchema,
    status: z.literal('published'),
    curriculumSchemaVersion: z.literal(2).optional(),
    legalSources: z.array(educationLegalSourceSchema).min(1),
    topics: z.array(educationTopicSchema).min(1),
    specialWorks: z.array(specialEducationWorkSchema).optional(),
    types: z
      .array(
        z
          .object({
            typeCode: entityIdSchema,
            category: statutoryEducationCategorySchema,
            audienceCode: entityIdSchema,
            title: nonEmptyTextSchema.max(200),
            audienceRule: nonEmptyTextSchema.max(1_000),
            durationRule: nonEmptyTextSchema.max(1_000),
            requiredMinutes: z.number().int().nonnegative().nullable(),
            requiredTopicIds: z.array(entityIdSchema),
            requiredEvidenceFieldIds: z.array(entityIdSchema).min(1),
            evaluationRequired: z.boolean(),
            certificateRequired: z.boolean(),
            specialWorkSelectionRequired: z.boolean().optional(),
            specialWorkScope: z.enum(['all-39', 'annex-1-38', 'annex-39']).optional(),
          })
          .strict(),
      )
      .min(1),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.effectiveUntil && value.effectiveUntil < value.effectiveFrom)
      context.addIssue({
        code: 'custom',
        path: ['effectiveUntil'],
        message: 'Standard effective period is reversed',
      });
    const codes = value.types.map((item) => item.typeCode);
    if (new Set(codes).size !== codes.length)
      context.addIssue({ code: 'custom', path: ['types'], message: 'Type codes must be unique' });
    const topicIds = value.topics.map((item) => item.topicId);
    if (new Set(topicIds).size !== topicIds.length)
      context.addIssue({ code: 'custom', path: ['topics'], message: 'Topic IDs must be unique' });
    const sourceIds = new Set(value.legalSources.map((item) => item.sourceId));
    value.topics.forEach((topic, index) => {
      topic.legalSourceIds.forEach((sourceId) => {
        if (!sourceIds.has(sourceId))
          context.addIssue({
            code: 'custom',
            path: ['topics', index, 'legalSourceIds'],
            message: `Topic legal source is missing: ${sourceId}`,
          });
      });
    });
    const publishedTopicIds = new Set(topicIds);
    value.types.forEach((type, index) => {
      type.requiredTopicIds.forEach((topicId) => {
        if (!publishedTopicIds.has(topicId))
          context.addIssue({
            code: 'custom',
            path: ['types', index, 'requiredTopicIds'],
            message: `Required topic is missing from the published catalog: ${topicId}`,
          });
      });
      const configured = new Set(type.requiredEvidenceFieldIds);
      statutoryRecordEvidenceFieldIds.forEach((field) => {
        if (!configured.has(field))
          context.addIssue({
            code: 'custom',
            path: ['types', index, 'requiredEvidenceFieldIds'],
            message: `Required statutory record field is missing: ${field}`,
          });
      });
    });
    if (value.curriculumSchemaVersion === 2) {
      const works = value.specialWorks ?? [];
      if (works.length !== 39)
        context.addIssue({
          code: 'custom',
          path: ['specialWorks'],
          message: 'Curriculum v2 must pin all 39 special-education works',
        });
      const workCodes = new Set<string>();
      const workOrdinals = new Set<number>();
      works.forEach((work, index) => {
        const expectedCode = `annex5-1-ra-${String(work.annexOrdinal).padStart(2, '0')}`;
        if (work.workCode !== expectedCode)
          context.addIssue({
            code: 'custom',
            path: ['specialWorks', index, 'workCode'],
            message: `Special-work code must match Annex 5 ordinal: ${expectedCode}`,
          });
        if (workCodes.has(work.workCode) || workOrdinals.has(work.annexOrdinal))
          context.addIssue({
            code: 'custom',
            path: ['specialWorks', index],
            message: 'Special-work codes and ordinals must be unique',
          });
        workCodes.add(work.workCode);
        workOrdinals.add(work.annexOrdinal);
        work.legalSourceIds.forEach((sourceId) => {
          if (!sourceIds.has(sourceId))
            context.addIssue({
              code: 'custom',
              path: ['specialWorks', index, 'legalSourceIds'],
              message: `Special-work legal source is missing: ${sourceId}`,
            });
        });
      });
      value.types.forEach((type, index) => {
        if (type.specialWorkSelectionRequired === undefined)
          context.addIssue({
            code: 'custom',
            path: ['types', index, 'specialWorkSelectionRequired'],
            message: 'Curriculum v2 must explicitly declare special-work selection',
          });
        if (type.specialWorkSelectionRequired && !type.specialWorkScope)
          context.addIssue({
            code: 'custom',
            path: ['types', index, 'specialWorkScope'],
            message: 'A special-work curriculum must declare its Annex 5 work scope',
          });
      });
      if (
        !value.legalSources.some(
          (source) => source.provision.includes('별표 5') && source.sourceArtifactSha256,
        )
      )
        context.addIssue({
          code: 'custom',
          path: ['legalSources'],
          message: 'Curriculum v2 requires a pinned Annex 5 source artifact hash',
        });
    }
  });

export const educationMaterialSchema = z
  .object({
    materialId: entityIdSchema,
    versionId: entityIdSchema,
    title: nonEmptyTextSchema.max(300),
    contentHash: sha256Schema,
    sourceDocumentId: entityIdSchema.nullable(),
  })
  .strict();

export const instructorSchema = z
  .object({
    instructorId: entityIdSchema,
    name: nonEmptyTextSchema.max(100),
    organizationName: nonEmptyTextSchema.max(200),
    qualificationReference: z.string().trim().max(500).nullable(),
    qualificationEvidence: z
      .object({
        basisCode: z.enum([
          'safety-health-manager',
          'supervisor',
          'safety-manager',
          'health-manager',
          'safety-health-officer',
          'occupational-physician',
          'kosha-instructor-course',
          'industrial-safety-health-consultant',
          'moel-knowledge-and-experience',
        ]),
        reference: nonEmptyTextSchema.max(500),
        verifiedAt: utcDateTimeSchema,
        verifiedByIdentityId: entityIdSchema,
      })
      .strict()
      .nullable(),
  })
  .strict();

export const plannedSessionSchema = z
  .object({
    sessionId: entityIdSchema,
    startsAt: utcDateTimeSchema,
    endsAt: utcDateTimeSchema,
    location: nonEmptyTextSchema.max(300),
    topicIds: z.array(entityIdSchema).min(1),
  })
  .strict()
  .superRefine((value, context) => {
    if (Date.parse(value.endsAt) <= Date.parse(value.startsAt))
      context.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'Session must end after start',
      });
  });

export const participantSnapshotSchema = z
  .object({
    participantId: entityIdSchema,
    identityId: entityIdSchema.nullable(),
    workforceReference: entityIdSchema,
    displayName: nonEmptyTextSchema.max(100),
    organizationId: entityIdSchema,
    organizationName: nonEmptyTextSchema.max(200),
  })
  .strict();

export const attendanceProofSchema = z
  .object({
    proofId: entityIdSchema,
    participantId: entityIdSchema,
    method: z.enum([
      'authenticated-self',
      'non-member-qr',
      'supervised-shared-device',
      'verified-certificate',
    ]),
    subjectIdentityId: entityIdSchema.nullable(),
    signedByIdentityId: entityIdSchema.nullable(),
    signedNameSnapshot: nonEmptyTextSchema.max(100),
    signedAt: utcDateTimeSchema,
    serverVerifiedAt: utcDateTimeSchema,
    oneTimeChallengeId: entityIdSchema,
    deviceSessionId: entityIdSchema,
    witnessIdentityId: entityIdSchema.nullable(),
    signatureImageEvidenceId: entityIdSchema.nullable(),
    certificateAttachmentId: entityIdSchema.nullable(),
    biometricTemplateCaptured: z.literal(false),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.method === 'authenticated-self' &&
      (!value.subjectIdentityId || value.subjectIdentityId !== value.signedByIdentityId)
    )
      context.addIssue({
        code: 'custom',
        path: ['signedByIdentityId'],
        message: 'Self attendance must be signed by the participant account',
      });
    if (
      (value.method === 'non-member-qr' || value.method === 'supervised-shared-device') &&
      (value.subjectIdentityId !== null || value.signedByIdentityId !== null)
    )
      context.addIssue({
        code: 'custom',
        path: ['signedByIdentityId'],
        message: 'A non-member claim is bound by its participant challenge, not another account',
      });
    if (value.method === 'supervised-shared-device' && !value.witnessIdentityId)
      context.addIssue({
        code: 'custom',
        path: ['witnessIdentityId'],
        message: 'Shared-device attendance requires a supervisor witness',
      });
    if (value.method === 'verified-certificate' && !value.certificateAttachmentId)
      context.addIssue({
        code: 'custom',
        path: ['certificateAttachmentId'],
        message: 'Certificate attendance requires the verified certificate',
      });
  });

export const attendanceEntrySchema = z
  .object({
    participant: participantSnapshotSchema,
    status: z.enum(['attended', 'partial', 'absent', 'exempt']),
    attendedMinutes: z.number().int().nonnegative(),
    proof: attendanceProofSchema.nullable(),
    followUpDueAt: utcDateTimeSchema.nullable(),
    exemptionReason: z.string().trim().max(500).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.status === 'absent' && !value.followUpDueAt)
      context.addIssue({
        code: 'custom',
        path: ['followUpDueAt'],
        message: 'Absence requires a follow-up due time',
      });
    if (value.status === 'exempt' && !value.exemptionReason)
      context.addIssue({
        code: 'custom',
        path: ['exemptionReason'],
        message: 'Exemption requires a reason',
      });
  });

export const educationPhotoSchema = z
  .object({
    evidenceId: entityIdSchema,
    mediaManifestId: entityIdSchema,
    contentHash: sha256Schema,
    capturedAt: utcDateTimeSchema,
    caption: nonEmptyTextSchema.max(500),
    serverCommitted: z.boolean(),
    immutable: z.boolean(),
  })
  .strict();

export const additionalEducationItemSchema = z
  .object({
    itemId: entityIdSchema,
    title: nonEmptyTextSchema.max(500),
    basis: z.enum(['site-risk', 'risk-assessment', 'incident-learning', 'other']),
    reference: z.string().trim().max(500).nullable(),
  })
  .strict();

export const evaluationResultSchema = z
  .object({
    participantId: entityIdSchema,
    assessedAt: utcDateTimeSchema,
    score: z.number().min(0).max(100).nullable(),
    result: z.enum(['passed', 'failed', 'not-completed']),
    evaluatorIdentityId: entityIdSchema,
  })
  .strict();

export const certificateSchema = z
  .object({
    certificateId: entityIdSchema,
    participantId: entityIdSchema,
    issuedAt: utcDateTimeSchema,
    attachmentId: entityIdSchema,
    verifiedByIdentityId: entityIdSchema,
  })
  .strict();

export const educationExecutionSchema = z
  .object({
    actualSessions: z.array(plannedSessionSchema).min(1),
    deliveredTopicIds: z.array(entityIdSchema).min(1),
    attendance: z.array(attendanceEntrySchema).min(1),
    photos: z.array(educationPhotoSchema).max(12),
    evaluations: z.array(evaluationResultSchema),
    certificates: z.array(certificateSchema),
    additionalEducationItems: z.array(additionalEducationItemSchema).max(50).optional(),
    note: z.string().trim().max(2_000).nullable(),
  })
  .strict();

export const educationReviewSchema = z
  .object({
    round: z.number().int().positive(),
    decision: z.enum(['pending', 'returned', 'approved']),
    submittedAt: utcDateTimeSchema,
    submittedBy: actorSnapshotSchema,
    submissionVersion: z.number().int().positive().nullable().optional(),
    submissionContentHash: sha256Schema.nullable().optional(),
    decidedAt: utcDateTimeSchema.nullable(),
    decidedBy: actorSnapshotSchema.nullable(),
    opinion: z.string().trim().max(2_000).nullable(),
  })
  .strict();

export const educationRecordOriginSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('new-authoring') }).strict(),
  z
    .object({
      kind: z.literal('legacy-import'),
      sourceCollection: z.literal('safetyEducationLogs'),
      sourceDocumentId: entityIdSchema,
      sourceSnapshotSha256: sha256Schema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('previous-form-attachment'),
      attachmentId: entityIdSchema,
      originalFileName: nonEmptyTextSchema.max(300),
      contentHash: sha256Schema,
      sourceFormDate: z.iso.date(),
      mediaManifestId: entityIdSchema,
      serverCommitted: z.boolean(),
      immutable: z.boolean(),
    })
    .strict(),
]);

export const educationRetentionSchema = z
  .object({
    policyId: entityIdSchema,
    policyVersion: nonEmptyTextSchema.max(100),
    classification: z.enum(['company-policy-pending', 'company-policy-approved']),
    retainUntil: z.iso.date().nullable(),
    legalMinimumClaim: z.string().trim().max(500).nullable(),
    disposition: z.literal('hold-no-auto-delete'),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.classification === 'company-policy-approved' && !value.retainUntil)
      context.addIssue({
        code: 'custom',
        path: ['retainUntil'],
        message: 'An approved retention policy requires a retain-until date',
      });
  });

export const educationRevisionLinkSchema = z
  .object({
    revisionOfEducationId: entityIdSchema,
    revisionOfContentHash: sha256Schema,
    reason: nonEmptyTextSchema.max(1_000),
  })
  .strict();

export const msdsEducationTriggers = [
  'worker-assigned-to-handling',
  'new-substance-introduced',
  'hazard-information-changed',
] as const;
export const msdsEducationTriggerSchema = z.enum(msdsEducationTriggers);
export const educationLegalContextSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('worker-education'),
      specialWorkCode: entityIdSchema.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('msds'),
      trigger: msdsEducationTriggerSchema,
      materialIds: z.array(entityIdSchema).min(1),
      sdsRevisionIds: z.array(entityIdSchema).min(1),
    })
    .strict(),
]);

export const historicalEducationLifecycleStates = [
  'legacy-draft-observed',
  'legacy-submitted-observed',
  'legacy-verified-observed',
  'legacy-rejected-observed',
  'legacy-unclassified-observed',
] as const;
export const historicalEducationLifecycleSchema = z
  .object({
    state: z.enum(historicalEducationLifecycleStates),
    /** A legacy status observation is never a current-system approval decision. */
    authoritativeApproval: z.literal(false),
    observedAt: utcDateTimeSchema.nullable(),
    basis: z
      .object({
        legacyStatus: z.string().trim().max(100).nullable(),
        sourceSnapshotSha256: sha256Schema,
        observation: z.enum([
          'legacy-status-only',
          'legacy-status-and-server-timestamp',
          'legacy-status-unrecognized',
        ]),
      })
      .strict(),
    source: z
      .object({
        collection: z.literal('safetyEducationLogs'),
        legacyId: entityIdSchema,
      })
      .strict(),
    importedAt: utcDateTimeSchema,
    editable: z.literal(false),
    correctionPolicy: z.literal('new-revision-only'),
  })
  .strict();

export const educationRecordSchema = z
  .object({
    educationId: entityIdSchema,
    version: z.number().int().positive(),
    documentVersionId: entityIdSchema,
    scope: resourceScopeSchema,
    stage: educationStageSchema,
    recordOrigin: educationRecordOriginSchema,
    retention: educationRetentionSchema,
    revision: educationRevisionLinkSchema.nullable(),
    title: nonEmptyTextSchema.max(300),
    standard: educationStandardSnapshotSchema,
    educationTypeCode: entityIdSchema,
    legalContext: educationLegalContextSchema.nullable().optional(),
    targetDescription: nonEmptyTextSchema.max(1_000),
    targetParticipants: z.array(participantSnapshotSchema).min(1),
    instructors: z.array(instructorSchema).min(1),
    materials: z.array(educationMaterialSchema).min(1),
    plannedSessions: z.array(plannedSessionSchema).min(1),
    execution: educationExecutionSchema.nullable(),
    reviews: z.array(educationReviewSchema),
    lock: z
      .object({
        contentHash: sha256Schema,
        lockedAt: utcDateTimeSchema,
        lockedBy: actorSnapshotSchema,
        lockedVersion: z.number().int().positive(),
      })
      .strict()
      .nullable(),
    legacy: z
      .object({ collection: z.literal('safetyEducationLogs'), legacyId: entityIdSchema })
      .strict()
      .nullable(),
    historicalLifecycle: historicalEducationLifecycleSchema.nullable().optional(),
    createdBy: actorSnapshotSchema,
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((value, context) => {
    const type = value.standard.types.find((item) => item.typeCode === value.educationTypeCode);
    if (!type)
      context.addIssue({
        code: 'custom',
        path: ['educationTypeCode'],
        message: 'Education type must exist in the pinned standard version',
      });
    if (type?.category === 'msds' && value.legalContext?.kind !== 'msds')
      context.addIssue({
        code: 'custom',
        path: ['legalContext'],
        message: 'MSDS education requires its statutory trigger and SDS revision bindings',
      });
    if (type?.category !== 'msds' && value.legalContext?.kind === 'msds')
      context.addIssue({
        code: 'custom',
        path: ['legalContext'],
        message: 'MSDS statutory context cannot be attached to another education category',
      });
    const selectedSpecialWorkCode =
      value.legalContext?.kind === 'worker-education' ? value.legalContext.specialWorkCode : null;
    if (
      type?.specialWorkSelectionRequired &&
      (!selectedSpecialWorkCode ||
        !value.standard.specialWorks?.some((work) => work.workCode === selectedSpecialWorkCode))
    )
      context.addIssue({
        code: 'custom',
        path: ['legalContext', 'specialWorkCode'],
        message: 'Special education requires a work code from the pinned 39-work catalog',
      });
    const selectedSpecialWork = value.standard.specialWorks?.find(
      (work) => work.workCode === selectedSpecialWorkCode,
    );
    if (
      selectedSpecialWork &&
      ((type?.specialWorkScope === 'annex-1-38' && selectedSpecialWork.annexOrdinal === 39) ||
        (type?.specialWorkScope === 'annex-39' && selectedSpecialWork.annexOrdinal !== 39))
    )
      context.addIssue({
        code: 'custom',
        path: ['legalContext', 'specialWorkCode'],
        message: 'Selected special work is outside this curriculum time branch',
      });
    if (
      value.standard.curriculumSchemaVersion === 2 &&
      value.execution &&
      value.execution.additionalEducationItems === undefined
    )
      context.addIssue({
        code: 'custom',
        path: ['execution', 'additionalEducationItems'],
        message: 'Curriculum v2 must explicitly record additional education items',
      });
    if (
      ['submitted', 'in-review', 'resubmitted', 'recorded', 'approved', 'closed'].includes(value.stage) &&
      !value.execution
    )
      context.addIssue({
        code: 'custom',
        path: ['execution'],
        message: 'Submitted education requires execution evidence',
      });
    if (['recorded', 'approved', 'closed'].includes(value.stage) !== (value.lock !== null))
      context.addIssue({
        code: 'custom',
        path: ['lock'],
        message: 'Approved education must have an integrity lock',
      });
    if (value.stage === 'recorded' && value.reviews.length !== 0)
      context.addIssue({
        code: 'custom',
        path: ['reviews'],
        message: 'Safety-authored recorded journals cannot contain a fabricated review decision',
      });
    if (value.historicalLifecycle) {
      if (
        value.stage !== 'draft' ||
        value.lock !== null ||
        value.reviews.length > 0 ||
        !value.legacy ||
        value.recordOrigin.kind !== 'legacy-import' ||
        value.recordOrigin.sourceCollection !== value.historicalLifecycle.source.collection ||
        value.recordOrigin.sourceDocumentId !== value.historicalLifecycle.source.legacyId ||
        value.recordOrigin.sourceSnapshotSha256 !==
          value.historicalLifecycle.basis.sourceSnapshotSha256 ||
        value.legacy.collection !== value.historicalLifecycle.source.collection ||
        value.legacy.legacyId !== value.historicalLifecycle.source.legacyId
      )
        context.addIssue({
          code: 'custom',
          path: ['historicalLifecycle'],
          message:
            'Historical lifecycle is a non-authoritative observation on an immutable draft import',
        });
      if (
        ['legacy-verified-observed', 'legacy-rejected-observed'].includes(
          value.historicalLifecycle.state,
        ) &&
        (!value.historicalLifecycle.observedAt ||
          value.historicalLifecycle.basis.observation !== 'legacy-status-and-server-timestamp')
      )
        context.addIssue({
          code: 'custom',
          path: ['historicalLifecycle', 'observedAt'],
          message: 'Verified or rejected legacy observations require their server timestamp',
        });
    }
    const targetIds = new Set(value.targetParticipants.map((item) => item.participantId));
    value.execution?.attendance.forEach((entry, index) => {
      if (!targetIds.has(entry.participant.participantId))
        context.addIssue({
          code: 'custom',
          path: ['execution', 'attendance', index],
          message: 'Attendance participant is outside the planned target',
        });
      if (entry.proof && entry.proof.participantId !== entry.participant.participantId)
        context.addIssue({
          code: 'custom',
          path: ['execution', 'attendance', index, 'proof', 'participantId'],
          message: 'Attendance challenge must be bound to this participant',
        });
    });
    const challenges = (value.execution?.attendance ?? [])
      .map((entry) => entry.proof?.oneTimeChallengeId)
      .filter((item): item is string => Boolean(item));
    if (new Set(challenges).size !== challenges.length)
      context.addIssue({
        code: 'custom',
        path: ['execution', 'attendance'],
        message: 'Each worker must use an individual one-time attendance challenge',
      });
  });

export type EducationStage = z.infer<typeof educationStageSchema>;
export type EducationStandardSnapshot = z.infer<typeof educationStandardSnapshotSchema>;
export type SpecialEducationWork = z.infer<typeof specialEducationWorkSchema>;
export type EducationExecution = z.infer<typeof educationExecutionSchema>;
export type EducationRecord = z.infer<typeof educationRecordSchema>;
export type AttendanceEntry = z.infer<typeof attendanceEntrySchema>;
export type StatutoryEducationCategory = z.infer<typeof statutoryEducationCategorySchema>;
export type EducationRecordOrigin = z.infer<typeof educationRecordOriginSchema>;
export type EducationLegalContext = z.infer<typeof educationLegalContextSchema>;
export type HistoricalEducationLifecycle = z.infer<typeof historicalEducationLifecycleSchema>;
